import React, { useMemo, useCallback } from 'react'
import { dayjs } from '@retailer-platform/shared-components'
import {
  DayjsDateParam,
  SimpleOptionsParam,
  compactObject,
  makeGenericStringQueryParamEncoder,
} from '@retailer-platform/dashboard/utils'
import {
  type ComparisonPeriod,
  DateRangePickerPresetOption,
  WeekStartDay,
} from '@retailer-platform/shared-components/src/common/date-range-picker/utils/DateRangePickerPresets.utils'
import { StringParam, useQueryParams } from 'use-query-params'
import { insightsSchemaConfigs } from '../../schemas/configuration/schema.configuration'
import { TimeIntervalDimensions } from '../../schemas/types/dimension.types'
import { InsightsWeekStartDayLocalStorageKey } from './filterParams.types'
import { FilterParamsContext } from './filterParamsContext'

export const useFilterParams = () => {
  // API V2 Filter params
  // get list of all Dimension names from all schemas, excluding date dimensions
  const allDimensions: Set<string> = useMemo(
    () =>
      Object.values(insightsSchemaConfigs).reduce((dimensions, { dimensionsConfig }) => {
        if (!dimensionsConfig) return dimensions

        Object.entries(dimensionsConfig)
          .filter(([, config]) => !config.date)
          .forEach(([dim]) => dimensions.add(dim))

        return dimensions
      }, new Set<string>()),
    []
  )

  // create query param config for each dimension
  const dimensionQueryParamConfig = useMemo(
    () => Object.fromEntries(Array.from(allDimensions).map(dim => [dim, SimpleOptionsParam])),
    [allDimensions]
  )

  const [selectedDimensionFilterValues, setSelectedDimensionFilterValues] =
    useQueryParams(dimensionQueryParamConfig)

  // Date filter params
  const [selectedDateFilterValues, setSelectedDateFilterValues] = useQueryParams({
    from: DayjsDateParam,
    to: DayjsDateParam,
    dateRange: makeGenericStringQueryParamEncoder<DateRangePickerPresetOption>(),
    comparisonType: makeGenericStringQueryParamEncoder<ComparisonPeriod>(),
    weekStartDay: makeGenericStringQueryParamEncoder<WeekStartDay>(),
    globalDateDimension: makeGenericStringQueryParamEncoder<TimeIntervalDimensions>(),
  })

  const [globalMetric, setGlobalMetric] = useQueryParams({
    globalMetric: StringParam,
  })

  const setDates: typeof setSelectedDateFilterValues = useCallback(
    values => {
      if (values['weekStartDay']) {
        localStorage.setItem(InsightsWeekStartDayLocalStorageKey, values['weekStartDay'] as string)
      }

      setSelectedDateFilterValues(values)
    },
    [setSelectedDateFilterValues]
  )

  const setGlobalDateDimension = (dimension: TimeIntervalDimensions) => {
    setSelectedDateFilterValues({ globalDateDimension: dimension })
  }

  const setGlobalMetricValue = (metric: string) => {
    setGlobalMetric({ globalMetric: metric })
  }

  const selectedDateValuesWithDefaults = Object.assign(
    {
      from: dayjs().subtract(1, 'week').startOf('day'),
      to: dayjs().subtract(1, 'days').endOf('day'),
      dateRange: DateRangePickerPresetOption.Last7Days,
      globalDateDimension: TimeIntervalDimensions.DATE_PT,
    },
    // Ensure weekStartDay is always a valid value
    {
      ...compactObject(selectedDateFilterValues),
      weekStartDay: (() => {
        // First check URL params
        const urlValue = selectedDateFilterValues.weekStartDay
        if (urlValue && Object.values(WeekStartDay).includes(urlValue)) {
          return urlValue
        }

        // Then check localStorage
        const storedValue = localStorage.getItem(InsightsWeekStartDayLocalStorageKey)
        if (storedValue && Object.values(WeekStartDay).includes(storedValue as WeekStartDay)) {
          return storedValue as WeekStartDay
        }

        // Default fallback
        return WeekStartDay.Monday
      })(),
    }
  )

  return {
    dates: selectedDateValuesWithDefaults,
    setDates,
    setGlobalDateDimension,
    globalMetric: globalMetric.globalMetric,
    setGlobalMetric: setGlobalMetricValue,

    // API V2
    selectedDimensionFilterValues,
    setSelectedDimensionFilterValues,
  }
}

export const FilterParamsProvider: React.FunctionComponent<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const filterParamsState = useFilterParams()

  return (
    <FilterParamsContext.Provider value={filterParamsState}>
      {children}
    </FilterParamsContext.Provider>
  )
}
